import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BASE_URL } from '../../config/env';
import { buildQueryParams } from '../../utils/object';

// Define the API slice
export const feedbackApi = createApi({
  reducerPath: 'feedbackApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.auth?.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['FeedbackQuestions', 'FeedbackResponses'],
  endpoints: (builder) => ({
    getFeedbackQuestions: builder.query({
      query: ({ page, limit, search, clientId } = {}) => ({
        url: 'feedback',
        params: buildQueryParams({ page, limit, search, client_id: clientId }),
      }),
      providesTags: ['FeedbackQuestions'],
    }),

    createFeedbackQuestion: builder.mutation({
      query: (newQuestion) => ({
        url: 'feedback/',
        method: 'POST',
        body: newQuestion,
      }),
      invalidatesTags: ['FeedbackQuestions'],
    }),

    updateFeedbackQuestion: builder.mutation({
      query: ({ id, updatedQuestion }) => ({
        url: `feedback/${id}`,
        method: 'PUT',
        body: updatedQuestion,
      }),
      invalidatesTags: ['FeedbackQuestions'],
    }),

    softDeleteFeedbackQuestion: builder.mutation({
      query: (id) => ({
        url: `feedback/${id}/soft-delete`,
        method: 'PATCH',
      }),
      invalidatesTags: ['FeedbackQuestions'],
    }),

    deleteFeedbackQuestion: builder.mutation({
      query: (id) => ({
        url: `feedback/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FeedbackQuestions'],
    }),

    getFeedbackResponses: builder.query({
      query: ({ userId, fromDate, toDate, page, limit, search, clientId } = {}) => ({
        url: 'feedback/responses',
        params: buildQueryParams({ user_id: userId, from_date: fromDate, to_date: toDate, page, limit, search, client_id: clientId }),
      }),
      providesTags: ['FeedbackResponses'],
    }),

    getUserFeedbackResponses: builder.query({
      query: (userId) => `feedback/responses/user/${userId}`,
    }),

    getFeedbackResponseDetails: builder.query({
      query: (responseId) => `feedback/responses/${responseId}`,
      providesTags: ['FeedbackResponses'],
    }),

    getFeedbackResponsesForQuestion: builder.query({
      query: (questionId) => `feedback/responses/question/${questionId}`,
    }),

    generateFeedbackReport: builder.query({
      query: () => 'feedback/report/',
    }),
  }),
});

export const {
  useGetFeedbackQuestionsQuery,
  useCreateFeedbackQuestionMutation,
  useUpdateFeedbackQuestionMutation,
  useSoftDeleteFeedbackQuestionMutation,
  useDeleteFeedbackQuestionMutation,
  useGetFeedbackResponsesQuery,
  useGetUserFeedbackResponsesQuery,
  useGetFeedbackResponseDetailsQuery,
  useLazyGetFeedbackResponseDetailsQuery,
  useGetFeedbackResponsesForQuestionQuery,
  useGenerateFeedbackReportQuery,
} = feedbackApi;
